import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Lock, Trash2, Shield } from 'lucide-react';
import { getStoreUrl } from '../utils/storeUrls';

/* ── CSS-art panels ─────────────────────────────────────────── */

const languageList = [
  { name: 'English', hi: true },
  { name: 'Español', hi: false },
  { name: 'Français', hi: false },
  { name: 'Deutsch', hi: false },
  { name: 'Português', hi: true },
  { name: 'Italiano', hi: false },
  { name: 'Nederlands', hi: false },
  { name: 'Svenska', hi: false },
  { name: 'Norsk', hi: false },
  { name: 'Polski', hi: false },
  { name: 'Čeština', hi: false },
  { name: '日本語', hi: true },
  { name: '한국어', hi: false },
  { name: '中文', hi: false },
  { name: 'हिन्दी', hi: false },
  { name: 'العربية', hi: false },
  { name: 'Türkçe', hi: false },
  { name: 'Tiếng Việt', hi: false },
  { name: '+13 more', hi: false },
];

const LanguagePillsArt = () => (
  <div
    style={{
      background: 'var(--v2-card)',
      border: '1px solid var(--v2-line)',
      borderRadius: '20px',
      padding: '32px 28px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      alignContent: 'flex-start',
    }}
  >
    {languageList.map((lang, i) => (
      <span
        key={i}
        style={{
          padding: '7px 16px',
          borderRadius: '999px',
          fontSize: '13px',
          fontWeight: 600,
          background: lang.hi ? 'var(--v2-primary)' : 'var(--v2-bg-subtle)',
          color: lang.hi ? '#fff' : 'var(--v2-ink-muted)',
          border: lang.hi ? '1px solid var(--v2-primary)' : '1px solid var(--v2-line)',
          transition: 'transform 0.15s',
        }}
      >
        {lang.name}
      </span>
    ))}
    <div
      style={{
        width: '100%',
        marginTop: '16px',
        padding: '12px 16px',
        borderRadius: '12px',
        background: 'var(--v2-primary-light)',
        border: '1px solid var(--v2-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '13px',
        color: 'var(--v2-primary)',
        fontWeight: 600,
      }}
    >
      <span style={{ fontSize: '18px' }}>🌐</span>
      30 languages supported · Whisper AI
    </div>
  </div>
);

const privacyRows = [
  { icon: <Ban className="w-5 h-5" />, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', key: 'onDevice' },
  { icon: <Lock className="w-5 h-5" />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', key: 'encrypted' },
  { icon: <Trash2 className="w-5 h-5" />, color: '#6b7280', bg: 'rgba(107,114,128,0.1)', key: 'delete' },
  { icon: <Shield className="w-5 h-5" />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', key: 'noSharing' },
];

const PrivacyArt = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: 'var(--v2-card)',
        border: '1px solid var(--v2-line)',
        borderRadius: '20px',
        padding: '32px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {privacyRows.map((row, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px',
            borderRadius: '12px',
            background: 'var(--v2-card)',
            border: '1px solid var(--v2-line)',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: row.bg,
              color: row.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {row.icon}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--v2-ink)' }}>{t(`featuresPage.privacy.rows.${row.key}.title`)}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--v2-ink-muted)', marginTop: '2px' }}>{t(`featuresPage.privacy.rows.${row.key}.sub`)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const summaryBullets = [
  'Pull API deadline forward; demo at All-Hands on Apr 22',
  'Ravi owns beta retention numbers by Friday EOD',
  'Cut free tier to 15 min/day; raise Pro to 10h',
  'Ship bullet/paragraph toggle in next release',
];

const SummaryArt = () => (
  <div
    style={{
      background: 'var(--v2-card)',
      border: '1px solid var(--v2-line)',
      borderRadius: '20px',
      padding: '24px',
      boxShadow: 'var(--v2-shadow-soft)',
    }}
  >
    <div
      style={{
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
        fontSize: '11px',
        fontWeight: 500,
        color: 'var(--v2-ink-muted)',
        letterSpacing: '0.04em',
        marginBottom: '12px',
      }}
    >
      Q2 ROADMAP SYNC · 58 MIN · APR 18
    </div>
    {summaryBullets.map((b, i) => (
      <div
        key={i}
        style={{
          background: 'var(--v2-bg-subtle)',
          borderRadius: '10px',
          padding: '10px 12px',
          fontSize: '13.5px',
          borderLeft: '3px solid var(--v2-primary)',
          marginBottom: '8px',
          color: 'var(--v2-ink-2)',
          lineHeight: 1.5,
        }}
      >
        {b}
      </div>
    ))}
  </div>
);

/* ── Shared section layout ─────────────────────────────────── */

interface SectionProps {
  label: string;
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaHref: string;
  art: React.ReactNode;
  reversed?: boolean;
}

const FeatureSection = ({
  label,
  title,
  description,
  features,
  cta,
  ctaHref,
  art,
  reversed = false,
}: SectionProps) => {
  const textCol = (
    <div>
      <div
        style={{
          color: 'var(--v2-primary)',
          fontWeight: 700,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: '12px',
        }}
      >
        {label}
      </div>
      <h2
        style={{
          fontSize: 'clamp(26px, 3vw, 38px)',
          fontWeight: 800,
          color: 'var(--v2-ink)',
          marginBottom: '14px',
          lineHeight: 1.15,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: '16px',
          color: 'var(--v2-ink-muted)',
          marginBottom: '24px',
          lineHeight: 1.65,
        }}
      >
        {description}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px' }}>
        {features.map((feature, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--v2-primary)',
                marginTop: '7px',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '14px', color: 'var(--v2-ink-muted)' }}>{feature}</span>
          </div>
        ))}
      </div>
      <a
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block',
          background: 'var(--v2-ink)',
          color: 'var(--v2-bg)',
          padding: '12px 24px',
          borderRadius: '999px',
          fontWeight: 700,
          fontSize: '14px',
          textDecoration: 'none',
          transition: 'opacity 0.15s',
        }}
      >
        {cta}
      </a>
    </div>
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '56px',
        alignItems: 'center',
        direction: reversed ? 'rtl' : 'ltr',
      }}
    >
      <div style={{ direction: 'ltr' }}>{textCol}</div>
      <div style={{ direction: 'ltr' }}>{art}</div>
    </div>
  );
};

/* ── Main component ────────────────────────────────────────── */

const FeatureSections = () => {
  const { t, i18n } = useTranslation();
  const storeUrl = getStoreUrl(i18n.language);

  const audioFeatures = Array.isArray(
    t('featureSections.audioFormat.features', { returnObjects: true })
  )
    ? (t('featureSections.audioFormat.features', { returnObjects: true }) as string[])
    : [
        'Instant AI transcription with 99% accuracy',
        'Smart summarization and key point extraction',
        'YouTube video summarization capability',
      ];

  const privacyFeatures = Array.isArray(
    t('featureSections.privacy.features', { returnObjects: true })
  )
    ? (t('featureSections.privacy.features', { returnObjects: true }) as string[])
    : [
        'End-to-end encryption for all your data',
        'Secure cross-device synchronization',
        'Complete data control and deletion rights',
      ];

  const summaryFeatures = Array.isArray(
    t('featureSections.summaries.features', { returnObjects: true })
  )
    ? (t('featureSections.summaries.features', { returnObjects: true }) as string[])
    : [
        'Meeting recaps with action items',
        'Key insights automatically extracted',
        'Custom summary templates',
      ];

  return (
    <div
      style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)', position: 'relative', zIndex: 2 }}
      className="px-4 py-16"
    >
      <div className="max-w-6xl mx-auto" style={{ display: 'flex', flexDirection: 'column', gap: '96px' }}>
        {/* Section 1 — Audio → Text (text left, art right) */}
        <FeatureSection
          label={t('featureSections.audioFormat.label')}
          title={t('featureSections.audioFormat.title')}
          description={t('featureSections.audioFormat.description')}
          features={audioFeatures}
          cta={t('featureSections.downloadNow')}
          ctaHref={storeUrl}
          art={<LanguagePillsArt />}
        />

        {/* Section 2 — Privacy (text right, art left) */}
        <FeatureSection
          label={t('featureSections.privacy.label')}
          title={t('featureSections.privacy.title')}
          description={t('featureSections.privacy.description')}
          features={privacyFeatures}
          cta={t('featureSections.downloadNow')}
          ctaHref={storeUrl}
          art={<PrivacyArt />}
          reversed
        />

        {/* Section 3 — Smart Summaries (text left, art right) */}
        <FeatureSection
          label={t('featureSections.summaries.label', 'SMART SUMMARIES')}
          title={t('featureSections.summaries.title', 'AI that reads between the lines')}
          description={t(
            'featureSections.summaries.description',
            'Notely AI doesn\'t just transcribe — it understands. Get instant meeting recaps, extracted action items, and key insights delivered the moment your recording ends.'
          )}
          features={summaryFeatures}
          cta={t('featureSections.summaries.cta', 'Download Now')}
          ctaHref={storeUrl}
          art={<SummaryArt />}
        />
      </div>
    </div>
  );
};

export default FeatureSections;
